/** @jsxImportSource @emotion/react */
import { Popover, PopoverContent, PopoverTrigger } from '@app/components/ui/Popover/Popover'
import { fetchGlossarySingleTerm } from '@app/services/strapiServices'
import { Link, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import {
  contentGridStyles,
  definitionStyles,
  footerLinkStyles,
  footerWrapperStyles,
  popoverStyles,
  termStyles,
  triggerStyles,
} from './GlossaryPopover.styles'

type GlossaryPopoverProps = {
  term: string
  children: React.ReactNode
}

export const GlossaryPopover = ({ term, children }: GlossaryPopoverProps) => {
  const [open, setOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['glossary-term', term],
    queryFn: () => fetchGlossarySingleTerm(term),
    enabled: open,
    staleTime: Infinity,
  })

  const title = data?.term ?? term
  const definition = data?.definition ?? ''
  const slug = data?.slug ?? term

  const openPopover = () => setOpen(true)
  const closePopover = () => setOpen(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          css={triggerStyles}
          onMouseEnter={openPopover}
          onMouseLeave={closePopover}
          onFocus={openPopover}
          onBlur={closePopover}
        >
          {children}
        </span>
      </PopoverTrigger>

      <PopoverContent
        side="top"
        sideOffset={0}
        css={popoverStyles}
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
      >
        {isLoading ? (
          <Typography variant="body2">Loading…</Typography>
        ) : (
          <div css={contentGridStyles}>
            <div css={termStyles}>{title}</div>
            <div css={definitionStyles}>{definition}</div>

            <div css={footerWrapperStyles}>
              <Link
                href={`/app/guide/glossary#${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                color="inherit"
              >
                <span css={footerLinkStyles}>
                  View in glossary <ArrowRight />
                </span>
              </Link>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
